import { create } from 'zustand';
import {
  clearSession,
  generateCommandId,
  sendCommand as sendCommandDirect,
  subscribeToAck,
  type AckState,
  type BotPresence,
  type ExtensionPresence,
  type SessionState,
  type SyncCommandInput
} from './client';
import { startTransport, type RealtimeTransport } from './transport';

// Single source of truth for the RTDB control-plane state on the web-app/embed
// side (see refs/realtime-sync). Every consumer shares ONE presence + sessions
// subscription via the ref-counted `connect()` action, instead of each hook
// opening its own `onValue` listener.
//
// Across realms (multiple embed iframes / tabs) the shared subscription is held
// by a single leader-elected frame via `leaderTransport`; followers get state
// over a BroadcastChannel. So the whole browser keeps ONE RTDB socket, not one
// per slide. `connect()` here is the per-realm ref-count over that transport.

const EMPTY_PRESENCE: ExtensionPresence = {
  installed: false, online: false, pages: {}, readers: []
};

export interface RealtimeSyncStore {
  presence: ExtensionPresence;
  // Live meeting bots for this owner, keyed by livestreamId. Read it through
  // useBotPresence rather than directly.
  bots: Record<string, BotPresence>;
  sessions: Record<string, SessionState>;

  // --- Phase 2 command dispatch ---
  // Ask the extension to start/stop reading chat for a livestream. Resolves once
  // the command is written to RTDB; the resulting state arrives via `sessions`.
  startReading: (
    livestreamId: string,
    opts?: Omit<SyncCommandInput, 'type' | 'livestreamId'>
  ) => Promise<void>;
  stopReading: (
    livestreamId: string,
    opts?: Pick<SyncCommandInput, 'commandId'>
  ) => Promise<void>;

  // Delete a livestream's session row (see client.clearSession). The disconnect
  // path calls this alongside `stopReading` so a command the extension never
  // consumes can't leave a `reading` row behind for its re-link pass to revive.
  clearSession: (livestreamId: string) => Promise<void>;

  // Watch a command's ack trail (see RealtimeTransport.watchAck). Rides the one
  // shared socket, so an embed frame that never signed in itself still sees the
  // extension's hops. Returns an unsubscribe.
  watchAck: (commandId: string, onAck: (ack: AckState | null) => void) => () => void;

  // Tell the leader whose app presence to publish (the current user/owner).
  // Safe to call before connect(); applied once the transport is running.
  setPresenceIdentity: (userId: string | null) => void;

  // Announce the current team owner key (from the teams store). A switch between
  // two known owners restarts the running transport so subscriptions, presence
  // and commands re-auth under the new `/sync/{ownerId}` instead of silently
  // staying on the previous owner's. Safe to call before connect().
  setOwnerKey: (ownerKey: string | null) => void;

  // --- Internal subscription manager (do not use from components) ---
  _refCount: number;
  _transport: RealtimeTransport | null;
  _presenceUserId: string | null;
  _ownerKey: string | null;
  // Open the shared subscription (idempotent, ref-counted). Returns a release
  // function; the last release tears the subscription down and resets state.
  connect: () => () => void;
}

// Every command gets an ack-able id. `sendCommandDirect` mints one when it's
// missing, but the transport path (SharedWorker `flushCommands` / the leader)
// writes the input as-is — so a command sent through a transport with no
// caller-supplied id landed in RTDB with no `commandId` and could never be
// acked or verified. That is how a dropped `stopReading` stayed invisible.
const withCommandId = <T extends Pick<SyncCommandInput, 'commandId'>>(opts: T): T => (
  opts.commandId ? opts : { ...opts, commandId: generateCommandId() }
);

export const useRealtimeSyncStore = create<RealtimeSyncStore>()((set, get) => {
  // Start a transport for the current owner key and register it on the store.
  const startStoreTransport = () => {
    const transport = startTransport(
      {
        onPresence: (presence) => set({ presence }),
        onBots: (bots) => set({ bots }),
        onSessions: (sessions) => set({ sessions })
      },
      get()._ownerKey
    );
    // Re-apply any identity registered before the transport started.
    transport.setPresenceIdentity(get()._presenceUserId);
    set({ _transport: transport });
  };

  return {
    presence: EMPTY_PRESENCE,
    bots: {},
    sessions: {},

    startReading: async (livestreamId, opts = {}) => {
      const command = { type: 'startReading' as const, livestreamId, ...withCommandId(opts) };
      const transport = get()._transport;
      // When connected, route through the leader's single socket; otherwise (no
      // active consumer) just write directly — one-off fire-and-forget.
      await (transport ? transport.sendCommand(command) : sendCommandDirect(command));
    },
    stopReading: async (livestreamId, opts = {}) => {
      const command = { type: 'stopReading' as const, livestreamId, ...withCommandId(opts) };
      const transport = get()._transport;
      await (transport ? transport.sendCommand(command) : sendCommandDirect(command));
    },

    clearSession: async (livestreamId) => {
      await clearSession(livestreamId);
    },

    watchAck: (commandId, onAck) => {
      const transport = get()._transport;
      // No active consumer (so no transport): fall back to a direct listener —
      // same shape as the command actions above.
      return transport
        ? transport.watchAck(commandId, onAck)
        : subscribeToAck(commandId, onAck);
    },

    setPresenceIdentity: (userId) => {
      set({ _presenceUserId: userId });
      get()._transport?.setPresenceIdentity(userId);
    },

    setOwnerKey: (ownerKey) => {
      const prev = get()._ownerKey;
      const next = ownerKey ? String(ownerKey) : null;
      if (prev === next) return;
      set({ _ownerKey: next });
      // Restart only on a switch between two *known* owners. The initial
      // null -> owner resolution doesn't invalidate anything (the first sign-in
      // fetches its token with the current auth headers anyway), and restarting
      // there would churn the connection on every page load.
      if (prev && next && get()._transport) {
        get()._transport?.stop();
        set({ presence: EMPTY_PRESENCE, bots: {}, sessions: {} });
        startStoreTransport();
      }
    },

    _refCount: 0,
    _transport: null,
    _presenceUserId: null,
    _ownerKey: null,
    connect: () => {
      const nextCount = get()._refCount + 1;
      set({ _refCount: nextCount });

      if (nextCount === 1) startStoreTransport();

      return () => {
        const remaining = get()._refCount - 1;
        if (remaining > 0) {
          set({ _refCount: remaining });
          return;
        }
        get()._transport?.stop();
        set({
          _refCount: 0,
          _transport: null,
          presence: EMPTY_PRESENCE,
          bots: {},
          sessions: {}
        });
      };
    }
  };
});
